// src/components/rating/RatingModal.jsx
import React, {
  useMemo,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { useTranslation } from "react-i18next";
import EventCard from "../events/EventCard";
import ReportForm from "../search/ReportForm";
import {
  AppModalButton,
  AppModalIconButton,
  appModalContentItemVariants,
} from "../common/AppModalShell";
import SocialCalibrationContent from "./SocialCalibrationContent";
import SlideToConfirm from "./SlideToConfirm";
import {
  MODAL_BACKDROP_CLASS,
  modalBackdropVariants,
  modalContentRootVariants,
  modalPanelVariants,
} from "../common/modalMotion";
import { useBodyLock } from "../../hooks/useDisableBodyScroll";
import { EvaluationContext } from "../../context/EvaluationContext.jsx";
import { RequiredActionsContext } from "../../context/RequiredActionsContext.jsx";
import { getCastleSkinMobileUrl } from "../../constants/skins";
import {
  applyBundledCastleSkinFallback,
  preloadCastleSkinUrls,
} from "../../utils/castleSkinAssets";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { toApiAbsoluteUrl } from "../../utils/ws";
import { isNativeApp } from "../../native/runtime";
import { formatCompactNumber } from "../../utils/compactNumber";
import {
  getPremiumCardThemeCssVars,
  normalizePremiumCardEffectIntensity,
  normalizePremiumCardTheme,
} from "../../constants/premiumCardThemes";
import "../../styles/RatingCardTheme.css";
import RatingCardMotion from "./RatingCardMotion";
import RatingPremiumCrown from "./RatingPremiumCrown";
import {
  buildRatingIntroPages,
  clearRatingIntroSession,
  getPeopleCompletionTransition,
  getRatingInitialPhase,
  getRatingIntroSessionKey,
  hasRatingServerProgress,
  PEOPLE_COMPLETION_TRANSITIONS,
  readRatingIntroSession,
  SOCIAL_ACTION_VALUATIONS,
  writeRatingIntroSession,
} from "./ratingFlow";

const ANIM_MODES = {
  SLIDE: "slide",
  SWIPE: "swipe",
  FADE: "fade",
};

// Небольшой эффект нажатия для всех кнопок
const TAP_FEEDBACK = {
  whileTap: { scale: 0.94 },
  transition: { type: "spring", stiffness: 500, damping: 30 },
};

const PRELOAD_COUNT = 3; // how many castle backgrounds to preload
const RATING_CARD_BASE_WIDTH_PX = 365;
const RATING_CARD_BASE_HEIGHT_PX = 610;
const RATING_MODAL_MIN_COMFORT_SCALE = 0.84;
const EVENT_STEP_RATING = "rating";
const EVENT_STEP_ATTENDANCE = "attendance";
const EVENT_STEP_ATTENDANCE_CONFIRM = "attendance_confirm";
const EVENT_STEP_REWARD = "reward";
const PEOPLE_REPORT_REASONS = [
  "offensive_behavior",
  "harassment",
  "fake_profile",
  "other",
];

const notifyVaultConnectionsChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("vault-connections-changed"));
};

function ModalCloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path
        d="M5.5 5.5L16.5 16.5M16.5 5.5L5.5 16.5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ModalBackIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 5L8 12L15 19"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PreferenceIntroIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path d="M5 8H23" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
      <path d="M5 14H23" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
      <path d="M5 20H23" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
      <circle cx="11" cy="8" r="2.3" fill="#1B2738" stroke="currentColor" strokeWidth="2.1" />
      <circle cx="17" cy="14" r="2.3" fill="#1B2738" stroke="currentColor" strokeWidth="2.1" />
      <circle cx="9" cy="20" r="2.3" fill="#1B2738" stroke="currentColor" strokeWidth="2.1" />
    </svg>
  );
}

function SafetyIntroIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path
        d="M14 3.5L22 6.7V12.4C22 17.5 18.7 22 14 24.5C9.3 22 6 17.5 6 12.4V6.7L14 3.5Z"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinejoin="round"
      />
      <path
        d="M14 17.8C14 17.8 10.5 15.6 10.5 13.3C10.5 12.2 11.3 11.4 12.3 11.4C13 11.4 13.6 11.8 14 12.4C14.4 11.8 15 11.4 15.7 11.4C16.7 11.4 17.5 12.2 17.5 13.3C17.5 15.6 14 17.8 14 17.8Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CompletionCheckIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" aria-hidden="true">
      <circle cx="26" cy="26" r="23" stroke="currentColor" strokeWidth="2.5" />
      <path
        d="M15.5 26.5L22.5 33.5L37 19"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const REWARD_CONFETTI = Array.from({ length: 34 }, (_, index) => {
  const angle = ((index * 47) % 360) * (Math.PI / 180);
  const distance = 88 + ((index * 29) % 122);

  return {
    id: index,
    color: ["#F8D56A", "#A993FF", "#F06FC8", "#7BA7FF", "#7DE2C5"][index % 5],
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance - 22,
    rotate: (index % 2 ? -1 : 1) * (140 + ((index * 37) % 220)),
    delay: (index % 8) * 0.025,
    width: index % 3 === 0 ? 5 : 7,
    height: index % 4 === 0 ? 13 : 8,
    radius: index % 3 === 0 ? "999px" : "2px",
  };
});

function RewardConfetti() {
  const burstOrigins = [
    { left: "34%", top: "48%", scale: 0.84 },
    { left: "66%", top: "48%", scale: 0.84 },
  ];

  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
      {burstOrigins.map((origin, burstIndex) => (
        <div
          key={`${origin.left}-${origin.top}`}
          className="absolute h-1 w-1"
          style={{ left: origin.left, top: origin.top }}
        >
          {REWARD_CONFETTI.map((particle) => (
            <motion.span
              key={`${burstIndex}-${particle.id}`}
              className="absolute left-0 top-0 block"
              style={{
                width: particle.width,
                height: particle.height,
                borderRadius: particle.radius,
                backgroundColor: particle.color,
                boxShadow: `0 0 9px ${particle.color}90`,
              }}
              initial={{ opacity: 0, x: 0, y: 0, scale: 0.2, rotate: 0 }}
              animate={{
                opacity: [0, 1, 1, 0],
                x: particle.x * origin.scale,
                y: particle.y * origin.scale,
                scale: [0.2, 1, 0.86],
                rotate: particle.rotate,
              }}
              transition={{
                duration: 1.55,
                delay: particle.delay + burstIndex * 0.08,
                ease: [0.18, 0.75, 0.32, 1],
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function RewardCelebrationIcon() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.35, rotate: -20 }}
      animate={{ opacity: [0, 1, 1, 0.82], scale: [0.35, 1.08, 0.96], rotate: 0 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      className="relative z-[1] flex h-[94px] w-[94px] items-center justify-center rounded-[30px] border border-[#F8D56A]/80 bg-[#F8D56A]/15 text-[54px] shadow-[0_0_52px_rgba(248,213,106,0.42)]"
      aria-hidden="true"
    >
      <span className="leading-none">🎉</span>
    </motion.div>
  );
}

const getEventRewardItems = (reward) => {
  const items = [];
  const coins = Number(reward?.coins || 0);
  if (coins > 0) {
    items.push({ kind: "coins", amount: coins });
  }

  for (let level = 1; level <= 5; level += 1) {
    const amount = Number(reward?.[`keys_level_${level}`] || 0);
    if (amount > 0) {
      items.push({ kind: "key", level, amount });
    }
  }

  return items;
};

function RatingCardSurface({ children, plain = false }) {
  if (plain) {
    return <>{children}</>;
  }

  return <div className="vg-rating-card-surface">{children}</div>;
}

const getCastleBgUrl = (castleSkinName) => {
  return getCastleSkinMobileUrl(castleSkinName || "Celestgar");
};

const mapPendingParticipant = (participant, isSport) => ({
  id: participant.user_id,
  userId: participant.user_id,
  storageId: participant.storage_id,
  code: participant.code,
  username: participant.username,
  avatarUrl: resolveUserAvatarSrc(
    participant,
    "/assets/avatars/paper-cap.webp"
  ),
  castleBgUrl: getCastleBgUrl(participant.castle_skin_name),
  isSport,
  hasPremiumSubscription: Boolean(participant.has_premium_subscription),
  premiumCardTheme: normalizePremiumCardTheme(participant.premium_card_theme),
  premiumCardEffectIntensity: normalizePremiumCardEffectIntensity(
    participant.premium_card_effect_intensity
  ),
  alreadyRatedSocial: Boolean(participant.already_rated_social),
  alreadyRatedSkill: Boolean(participant.already_rated_skill),
  requireSocial: participant.require_social !== false,
  requireSkill: Boolean(participant.require_skill),
});

const getPendingStageRequirements = (payload) => ({
  requireSocial: Boolean(
    payload?.ui?.people_flow?.decision_required
      ? payload?.pending?.require_social
      : payload?.pending?.stage_require_social ?? payload?.pending?.require_social
  ),
  requireSkill: Boolean(
    payload?.ui?.people_flow?.decision_required
      ? payload?.pending?.require_skill
      : payload?.pending?.stage_require_skill ?? payload?.pending?.require_skill
  ),
});

const mapPendingParticipants = (payload) => {
  if (!payload?.participants?.length) return [];
  const isSport = Boolean(payload?.event?.sport_type);
  return payload.participants.map((participant) =>
    mapPendingParticipant(participant, isSport)
  );
};

const getEventImageUrl = (rawUrl) => {
  const value = String(rawUrl || "").trim();
  if (!value) return "";
  if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) return value;
  if (value.startsWith("/assets/")) return value;
  return toApiAbsoluteUrl(value) || "";
};

const getViewportMetrics = () => {
  if (typeof window === "undefined") {
    return { width: 390, height: 844 };
  }
  const vv = window.visualViewport;
  return {
    width: Math.max(0, Number(vv?.width || window.innerWidth || 390)),
    height: Math.max(0, Number(vv?.height || window.innerHeight || 844)),
  };
};

const getMobileBottomNavReservePx = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }

  const navNode = document.getElementById("bottom-nav");
  if (!(navNode instanceof HTMLElement)) {
    return 0;
  }

  const rect = navNode.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return 0;
  }

  const rawClearance = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue("--bottom-nav-clearance");
  const clearance = Number.parseFloat(rawClearance);
  return Number.isFinite(clearance) ? Math.max(0, clearance) : 0;
};

const cn = (...classes) => classes.filter(Boolean).join(" ");

const getRatingSessionStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch (_error) {
    return null;
  }
};

const RatingModal = ({ initialIntroKind = null, suppressed = false }) => {
  const { t } = useTranslation(["rating", "search"]);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user: viewerUser,
    pending,
    refresh,
    open,
    setOpen,
  } = useContext(EvaluationContext);
  const { refresh: refreshRequiredActions } = useContext(RequiredActionsContext);


const hasPending = Boolean(
  pending?.has_pending &&
  pending?.event &&
  (
    (pending?.participants && pending.participants.length > 0) ||
    (pending?.pending?.require_event_rating && !pending?.pending?.event_done) ||
    pending?.ui?.people_flow?.decision_required ||
    pending?.ui?.completion_only
  )
);

  const isOpen = open && hasPending;
  const isVisible = isOpen && !suppressed;

  useBodyLock(isVisible);


  // --- backend data from /ratings/pending/ via EvaluationContext ---
  const backendEvent = useMemo(() => {
    if (!pending?.event) return null;
    return {
      id: pending.event.id,
      title: pending.event.name || "",
      location: "",
      dateTime: pending.event.date_time || "",
      isSport: Boolean(pending.event.sport_type),
      badgeLabelId: pending.event.badge_label_id ?? pending.event.badge_label ?? null,
      imageUrl: getEventImageUrl(
        pending.event.hero_image_url ||
          pending.event.image_url ||
          pending.event.photo_url ||
          pending.event.avatar_url ||
          pending.event.picture_url
      ),
    };
  }, [pending]);

const backendPeople = useMemo(() => {
  return mapPendingParticipants(pending);
}, [pending]);
  const [people, setPeople] = useState([]);
  const selfPreviewPerson = useMemo(() => {
    const card = pending?.viewer_card || viewerUser || {};
    const castleSkinName = card.castle_skin_name || card.castle_skin;

    return {
      id: card.user_id || card.id || "viewer",
      userId: card.user_id || card.id || null,
      code: card.code || "---",
      username: card.username || "",
      avatarUrl: resolveUserAvatarSrc(card, "/assets/avatars/paper-cap.webp"),
      castleBgUrl: getCastleBgUrl(castleSkinName),
      hasPremiumSubscription: Boolean(card.has_premium_subscription),
      premiumCardTheme: normalizePremiumCardTheme(card.premium_card_theme),
      premiumCardEffectIntensity: normalizePremiumCardEffectIntensity(
        card.premium_card_effect_intensity
      ),
    };
  }, [pending?.viewer_card, viewerUser]);
  const event = backendEvent;
  const initialStageRequirements = getPendingStageRequirements(pending);
  const [stageRequirements, setStageRequirements] = useState(
    initialStageRequirements
  );
  const [peopleFlow, setPeopleFlow] = useState(
    pending?.ui?.people_flow || null
  );
  const requireSocial = stageRequirements.requireSocial;
  const requireSkill = stageRequirements.requireSkill;
  const isOptionalPeopleStage = peopleFlow?.stage === "optional";
  const viewerIsHost = Boolean(pending?.ui?.viewer_is_host);
  const evaluationIntroSessionKey = useMemo(
    () =>
      getRatingIntroSessionKey({
        userId:
          pending?.viewer_card?.user_id ||
          viewerUser?.id ||
          viewerUser?.user_id,
        eventId: pending?.event?.id,
        requireSocial: Boolean(pending?.pending?.require_social),
        requireSkill: Boolean(pending?.pending?.require_skill),
      }),
    [
      pending?.event?.id,
      pending?.pending?.require_skill,
      pending?.pending?.require_social,
      pending?.viewer_card?.user_id,
      viewerUser?.id,
      viewerUser?.user_id,
    ]
  );
  const evaluationTaskIdentity =
    evaluationIntroSessionKey ||
    (pending?.event?.id
      ? `rating-task:${pending.event.id}:${requireSocial}:${requireSkill}`
      : null);
  const initializedTaskIdentityRef = useRef(null);

  // Preload castle backgrounds for the first few people
  useEffect(() => {
    if (!people.length) return;

    void preloadCastleSkinUrls(
      people.slice(0, PRELOAD_COUNT).map((person) => person.castleBgUrl)
    );
  }, [people]);

  useEffect(() => {
    if (!selfPreviewPerson?.castleBgUrl) return;
    void preloadCastleSkinUrls([selfPreviewPerson.castleBgUrl]);
  }, [selfPreviewPerson?.castleBgUrl]);

  // --- animation modes ---
  const [animMode] = useState(ANIM_MODES.SLIDE);
  const [direction, setDirection] = useState(0); // -1 left, 1 right, 0 neutral
  const [completedModesByUserId, setCompletedModesByUserId] = useState({});
  const [introStep, setIntroStep] = useState(0);
  const [resumePhaseFromIntro, setResumePhaseFromIntro] = useState(null);
  const [guideOnlyAfterChoice, setGuideOnlyAfterChoice] = useState(false);

  const getCardVariants = (mode) => {
    if (mode === ANIM_MODES.SWIPE) {
      return {
        enter: (dir) => ({
          x: dir === 0 ? 0 : dir * 140,
          opacity: 0,
          scale: 0.98,
          rotate: dir === 0 ? 0 : dir * 4,
          filter: "blur(2px)",
        }),
        center: {
          x: 0,
          opacity: 1,
          scale: 1,
          rotate: 0,
          filter: "blur(0px)",
          transition: { duration: 0.24, ease: "easeOut" },
        },
        exit: (dir) => ({
          x: dir === 0 ? 0 : dir * -220,
          opacity: 0,
          scale: 0.98,
          rotate: dir === 0 ? 0 : dir * -8,
          filter: "blur(3px)",
          transition: { duration: 0.18, ease: "easeIn" },
        }),
      };
    }

    if (mode === ANIM_MODES.FADE) {
      return {
        enter: () => ({ opacity: 0, scale: 0.995, filter: "blur(1px)" }),
        center: {
          opacity: 1,
          scale: 1,
          filter: "blur(0px)",
          transition: { duration: 0.2, ease: "easeOut" },
        },
        exit: () => ({
          opacity: 0,
          scale: 0.995,
          filter: "blur(1px)",
          transition: { duration: 0.16, ease: "easeIn" },
        }),
      };
    }

    // Default: SLIDE
    return {
      enter: (dir) => ({
        x: dir === 0 ? 0 : dir * 70,
        opacity: 0,
        scale: 0.99,
        filter: "blur(1px)",
      }),
      center: {
        x: 0,
        opacity: 1,
        scale: 1,
        filter: "blur(0px)",
        transition: { duration: 0.22, ease: "easeOut" },
      },
      exit: (dir) => ({
        x: dir === 0 ? 0 : dir * -160,
        opacity: 0,
        scale: 0.99,
        filter: "blur(2px)",
        transition: { duration: 0.18, ease: "easeIn" },
      }),
    };
  };

  const variants = getCardVariants(animMode);

  const [idx, setIdx] = useState(0);
  const [forcedEvaluationMode, setForcedEvaluationMode] = useState(null);
  const current = people[idx] || people[0];
  const calibrationUser = viewerUser || current || null;
  const isSportEvent =
    Boolean(event?.isSport) || Boolean(current?.isSport || false);
  const introSteps = useMemo(() => {
    const steps = [];
    if (requireSocial) {
      steps.push("social");
    }
    if (requireSkill) {
      steps.push("skill");
    }
    if (!steps.length) {
      steps.push(isSportEvent ? "skill" : "social");
    }
    return steps;
  }, [isSportEvent, requireSkill, requireSocial]);
  const introPages = useMemo(
    () =>
      buildRatingIntroPages({
        requireSocial,
        requireSkill,
        isSportEvent,
      }),
    [isSportEvent, requireSkill, requireSocial]
  );
  const activeIntroPage =
    introPages[Math.min(introStep, Math.max(0, introPages.length - 1))] ||
    { mode: "social", kind: "event" };
  const activeIntroMode =
    activeIntroPage.mode || "social";
  const activeIntroKind = activeIntroPage.kind || "event";
  const requestedInitialIntroStep = useMemo(() => {
    if (!initialIntroKind) return 0;
    const matchingIndex = introPages.findIndex(
      (page) => page.kind === initialIntroKind
    );
    return matchingIndex >= 0 ? matchingIndex : 0;
  }, [initialIntroKind, introPages]);
  const isSocialPreferencesCalibrated = Boolean(
    viewerUser?.social_preferences_calibrated === true ||
      viewerUser?.social_calibration_status === "stable"
  );
  const calibrationCopy = useMemo(
    () => {
      const copyKey = isSocialPreferencesCalibrated
        ? "calibration_complete"
        : "calibration";

      return {
        title: t(`modal.intro.${copyKey}.title`, {
          defaultValue: isSocialPreferencesCalibrated
            ? "Connections"
            : "Calibration",
        }),
        topText: t(`modal.intro.${copyKey}.top_text`, {
          defaultValue: isSocialPreferencesCalibrated
            ? "Your social preference calibration is complete."
            : "VaultGrid is currently learning which people and events fit you better.",
        }),
        bottomText: t(`modal.intro.${copyKey}.bottom_text`, {
          defaultValue: isSocialPreferencesCalibrated
            ? "Keep reviewing participant cards and choosing the people who feel like the best match. This helps fine-tune your preferences even further."
            : "Please attend a few more events and complete next steps to help VaultGrid finalize your social preferences.",
        }),
      };
    },
    [isSocialPreferencesCalibrated, t]
  );
  const requiresEventRating = Boolean(
    pending?.pending?.require_event_rating && !pending?.pending?.event_done
  );
  const getCurrentUserId = (person = current) =>
    person?.userId || person?.id || person?.user_id || null;
  const getPersonEvaluationProgress = (person) => {
    const userId = getCurrentUserId(person);
    const localProgress = userId ? completedModesByUserId[userId] || {} : {};
    return {
      socialDone: Boolean(person?.alreadyRatedSocial || localProgress.socialDone),
      skillDone: Boolean(person?.alreadyRatedSkill || localProgress.skillDone),
    };
  };
  const currentProgress = getPersonEvaluationProgress(current);
  const currentEvaluationMode = useMemo(() => {
    if (
      forcedEvaluationMode === "social" &&
      requireSocial &&
      currentProgress.socialDone
    ) {
      return "social";
    }
    if (
      forcedEvaluationMode === "skill" &&
      requireSkill &&
      currentProgress.skillDone
    ) {
      return "skill";
    }
    if (requireSocial && !currentProgress.socialDone) {
      return "social";
    }
    if (requireSkill && !currentProgress.skillDone) {
      return "skill";
    }
    return introSteps[introSteps.length - 1] || "social";
  }, [
    currentProgress.skillDone,
    currentProgress.socialDone,
    forcedEvaluationMode,
    introSteps,
    requireSkill,
    requireSocial,
  ]);

  // When switching people, preload a few castle backgrounds ahead of the current index
  useEffect(() => {
    if (!people.length) return;

    const start = idx + 1;
    const end = Math.min(people.length, idx + 1 + PRELOAD_COUNT);

    void preloadCastleSkinUrls(
      people.slice(start, end).map((person) => person.castleBgUrl)
    );
  }, [idx, people]);

  // phase: "intro" | "people" | "people_choice" | "event" | "complete"
  const [phase, setPhase] = useState("intro");
  const [eventStep, setEventStep] = useState(EVENT_STEP_RATING);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportMode, setReportMode] = useState("user"); // "user" | "event"
  const [peopleReportOpen, setPeopleReportOpen] = useState(false);
  const [peopleReportStep, setPeopleReportStep] = useState("reasons");
  const [peopleReportReason, setPeopleReportReason] = useState("");
  const [peopleReportComment, setPeopleReportComment] = useState("");
  const [peopleReportSubmitting, setPeopleReportSubmitting] = useState(false);
  const peopleReportInFlightRef = useRef(false);
  const [peopleReportError, setPeopleReportError] = useState("");
  const [peopleActionSubmitting, setPeopleActionSubmitting] = useState(false);
  const peopleActionInFlightRef = useRef(false);
  const [peopleChoiceSubmitting, setPeopleChoiceSubmitting] = useState(false);
  const peopleChoiceInFlightRef = useRef(false);
  const [viewportMetrics, setViewportMetrics] = useState(getViewportMetrics);
  const isSelfPreview =
    phase === "intro" && activeIntroKind === "self_preview";
  const displayedCardPerson = isSelfPreview ? selfPreviewPerson : current;
  const hasPremiumCardAccess =
    (phase === "people" || isSelfPreview) &&
    Boolean(displayedCardPerson?.hasPremiumSubscription);
  const isPremiumTheme =
    hasPremiumCardAccess &&
    displayedCardPerson?.premiumCardEffectIntensity !== "off";
  const premiumCardThemeStyle = useMemo(
    () =>
      displayedCardPerson?.hasPremiumSubscription
        ? getPremiumCardThemeCssVars(
            displayedCardPerson?.premiumCardTheme,
            displayedCardPerson?.premiumCardEffectIntensity
          )
        : undefined,
    [
      displayedCardPerson?.hasPremiumSubscription,
      displayedCardPerson?.premiumCardEffectIntensity,
      displayedCardPerson?.premiumCardTheme,
    ]
  );

  const canGoBack =
    phase === "intro"
      ? !guideOnlyAfterChoice && introStep > 0
      : phase === "people" ||
        (phase === "people_choice" && people.length > 0) ||
        (phase === "complete" && people.length > 0) ||
        (phase === "event" &&
          eventStep !== EVENT_STEP_REWARD &&
          (eventStep === EVENT_STEP_ATTENDANCE ||
            eventStep === EVENT_STEP_ATTENDANCE_CONFIRM ||
            people.length > 0));

  useEffect(() => {
    if (!isOpen || !evaluationTaskIdentity) {
      return;
    }

    // A refreshed payload for the same pending task must never restart the
    // modal. Android WebViews trigger these refreshes more often on resume.
    if (initializedTaskIdentityRef.current === evaluationTaskIdentity) {
      return;
    }
    initializedTaskIdentityRef.current = evaluationTaskIdentity;

    const introSession = readRatingIntroSession(
      getRatingSessionStorage(),
      evaluationIntroSessionKey
    );
    const hasServerProgress = hasRatingServerProgress({
      pending: pending?.pending,
      peopleFlow: pending?.ui?.people_flow,
      participants: backendPeople,
    });
    const introCompleted = Boolean(
      introSession?.introCompleted || hasServerProgress
    );
    const initialPhase = getRatingInitialPhase({
      hasParticipants: backendPeople.length > 0,
      requiresEventRating,
      introCompleted,
      hasServerProgress,
      decisionRequired: Boolean(
        pending?.ui?.people_flow?.decision_required
      ),
    });
    const resolvedInitialPhase = pending?.ui?.completion_only
      ? "complete"
      : initialPhase;
    const restoredIntroStep = introCompleted
      ? Math.max(0, introPages.length - 1)
      : Math.min(
          Math.max(0, introPages.length - 1),
          Math.max(requestedInitialIntroStep, introSession?.introStep || 0)
        );

    if (hasServerProgress) {
      writeRatingIntroSession(
        getRatingSessionStorage(),
        evaluationIntroSessionKey,
        {
          introCompleted: true,
          introStep: Math.max(0, introPages.length - 1),
        }
      );
    }

    setIdx(0);
    setPeople(backendPeople);
    setPhase(resolvedInitialPhase);
    setStageRequirements(getPendingStageRequirements(pending));
    setPeopleFlow(pending?.ui?.people_flow || null);
    setDirection(0);
    setIntroStep(restoredIntroStep);
    setForcedEvaluationMode(null);
    setResumePhaseFromIntro(null);
    setGuideOnlyAfterChoice(false);
    setCompletedModesByUserId({});
    setEventRating(pending?.ui?.event_rating_default_value ?? 1);
    setEventRatingSubmitting(false);
    eventRatingInFlightRef.current = false;
    setEventStep(EVENT_STEP_RATING);
    setEventReward(null);
    setEventRewardClosing(false);
    completionClosingRef.current = false;
    setCompletionClosing(false);
    setHostFeedbackEnabled(false);
    setHostFeedbackText("");
    setSawUnregisteredAttendees(null);
    setPeopleReportOpen(false);
    setPeopleReportStep("reasons");
    setPeopleReportReason("");
    setPeopleReportComment("");
    setPeopleReportSubmitting(false);
    peopleReportInFlightRef.current = false;
    setPeopleReportError("");
    peopleActionInFlightRef.current = false;
    setPeopleActionSubmitting(false);
    peopleChoiceInFlightRef.current = false;
    setPeopleChoiceSubmitting(false);
  }, [
    isOpen,
    backendPeople,
    evaluationIntroSessionKey,
    evaluationTaskIdentity,
    introPages.length,
    pending,
    pending?.event?.id,
    pending?.pending?.event_done,
    pending?.pending?.skill_done,
    pending?.pending?.social_done,
    pending?.pending?.stage_require_skill,
    pending?.pending?.stage_require_social,
    pending?.ui?.event_rating_default_value,
    pending?.ui?.completion_only,
    pending?.ui?.people_flow,
    requiresEventRating,
    requestedInitialIntroStep,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncMetrics = () => setViewportMetrics(getViewportMetrics());

    syncMetrics();
    window.addEventListener("resize", syncMetrics);
    window.addEventListener("orientationchange", syncMetrics);
    window.visualViewport?.addEventListener("resize", syncMetrics);
    window.visualViewport?.addEventListener("scroll", syncMetrics);

    return () => {
      window.removeEventListener("resize", syncMetrics);
      window.removeEventListener("orientationchange", syncMetrics);
      window.visualViewport?.removeEventListener("resize", syncMetrics);
      window.visualViewport?.removeEventListener("scroll", syncMetrics);
    };
  }, []);

  const modalCardLayout = useMemo(() => {
    const viewportWidth = Math.max(280, viewportMetrics.width || 390);
    const viewportHeight = Math.max(320, viewportMetrics.height || 844);
    const bottomNavReservePx =
      viewportWidth < 1024 && !isNativeApp() ? getMobileBottomNavReservePx() : 0;

    const horizontalGutterPx = 24;
    const verticalGutterPx = 24;
    const availableWidth = Math.max(1, viewportWidth - horizontalGutterPx);
    const availableHeight = Math.max(
      1,
      viewportHeight - verticalGutterPx - bottomNavReservePx
    );

    const scaleByWidth = availableWidth / RATING_CARD_BASE_WIDTH_PX;
    const scaleByHeight = availableHeight / RATING_CARD_BASE_HEIGHT_PX;
    const rawScale = Math.min(1, scaleByWidth, scaleByHeight);

    const scale =
      rawScale >= RATING_MODAL_MIN_COMFORT_SCALE
        ? rawScale
        : scaleByWidth < RATING_MODAL_MIN_COMFORT_SCALE
        ? scaleByWidth
        : RATING_MODAL_MIN_COMFORT_SCALE;

    const normalizedScale = Math.max(0.68, Math.min(1, Number(scale.toFixed(3))));
    const needsVerticalScroll = scaleByHeight < normalizedScale - 0.001;
    const preferTopAlignedLayout = normalizedScale < 0.995 || needsVerticalScroll;

    return {
      scale: normalizedScale,
      needsVerticalScroll,
      preferTopAlignedLayout,
      bottomNavReservePx,
    };
  }, [viewportMetrics.height, viewportMetrics.width]);

  // mark toggle with backend favourites (StorageFavourite)
  const [markedById, setMarkedById] = useState(() => ({}));
  const isMarked = Boolean(current?.id && markedById[current.id]);

  // event rating (last screen)
  const [eventRating, setEventRating] = useState(
    pending?.ui?.event_rating_default_value ?? 1
  );
  const [eventRatingSubmitting, setEventRatingSubmitting] = useState(false);
  const eventRatingInFlightRef = useRef(false);
  const [hostFeedbackEnabled, setHostFeedbackEnabled] = useState(false);
  const [hostFeedbackText, setHostFeedbackText] = useState("");
  const [sawUnregisteredAttendees, setSawUnregisteredAttendees] = useState(null);
  const [eventReward, setEventReward] = useState(null);
  const [eventRewardClosing, setEventRewardClosing] = useState(false);
  const [completionClosing, setCompletionClosing] = useState(false);
  const completionClosingRef = useRef(false);
  const shouldAskUnregisteredAttendees = Boolean(
    pending?.ui?.ask_unregistered_attendees
  );

  const submitPendingPeopleRating = async (
    payload,
    logLabel,
    endpoint = "/ratings/pending/submit/"
  ) => {
    if (peopleActionInFlightRef.current) return null;
    peopleActionInFlightRef.current = true;
    setPeopleActionSubmitting(true);
    try {
      const response = await api.post(endpoint, payload, {
        meta: { suppressGlobalError: true },
      });
      return response?.data || {};
    } catch (error) {
      console.error(logLabel, error);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              status: error?.response?.status,
              message:
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                t("common:something_went_wrong", "Something went wrong"),
            },
          })
        );
      }
      return null;
    } finally {
      peopleActionInFlightRef.current = false;
      setPeopleActionSubmitting(false);
    }
  };

  const handleBack = () => {
    if (
      peopleActionInFlightRef.current ||
      peopleReportInFlightRef.current ||
      eventRatingInFlightRef.current ||
      peopleActionSubmitting ||
      peopleReportSubmitting ||
      eventRatingSubmitting
    ) {
      return;
    }

    if (peopleReportOpen) {
      if (peopleReportStep === "details") {
        setPeopleReportStep("reasons");
        setPeopleReportReason("");
        setPeopleReportError("");
        return;
      }
      setPeopleReportOpen(false);
      setPeopleReportStep("reasons");
      setPeopleReportReason("");
      setPeopleReportComment("");
      setPeopleReportError("");
      return;
    }

    if (!canGoBack) {
      return;
    }

    if (phase === "intro") {
      setIntroStep((prev) => {
        const nextStep = Math.max(0, prev - 1);
        const storedIntroSession = readRatingIntroSession(
          getRatingSessionStorage(),
          evaluationIntroSessionKey
        );
        writeRatingIntroSession(
          getRatingSessionStorage(),
          evaluationIntroSessionKey,
          {
            introCompleted: storedIntroSession?.introCompleted === true,
            introStep: nextStep,
          }
        );
        return nextStep;
      });
      return;
    }

    if (phase === "complete") {
      setDirection(-1);
      setPhase("people");
      setIdx(Math.max(0, people.length - 1));
      setForcedEvaluationMode(null);
      return;
    }

    if (phase === "people_choice") {
      if (!people.length) return;
      setDirection(-1);
      setPhase("people");
      setIdx(Math.max(0, people.length - 1));
      setForcedEvaluationMode(null);
      return;
    }

    if (phase === "event") {
      if (eventStep === EVENT_STEP_REWARD) {
        return;
      }
      if (eventStep === EVENT_STEP_ATTENDANCE_CONFIRM) {
        setSawUnregisteredAttendees(null);
        setEventStep(EVENT_STEP_ATTENDANCE);
        return;
      }
      if (eventStep === EVENT_STEP_ATTENDANCE) {
        setEventStep(EVENT_STEP_RATING);
        return;
      }
      setDirection(-1);
      setPhase("people");
      setIdx(people.length - 1);
      setForcedEvaluationMode(null);
      return;
    }

    if (phase === "people") {
      if (
        currentEvaluationMode === "skill" &&
        requireSocial &&
        currentProgress.socialDone
      ) {
        setDirection(-1);
        setForcedEvaluationMode("social");
        return;
      }

      if (idx === 0) {
        setResumePhaseFromIntro("people");
        setIntroStep(Math.max(0, introPages.length - 1));
        setPhase("intro");
        setForcedEvaluationMode(null);
        return;
      }
      setDirection(-1);
      setForcedEvaluationMode(null);
      setIdx(idx - 1);
    }
  };

  const showPeopleCompletion = (dir = 0) => {
    setDirection(dir);
    setForcedEvaluationMode(null);
    setPhase("complete");
  };

  const reconcilePeopleQueue = async (
    dir = 0,
    { showGuideBeforePeople = false } = {}
  ) => {
    if (peopleActionInFlightRef.current) return;
    peopleActionInFlightRef.current = true;
    setPeopleActionSubmitting(true);

    try {
      const response = await api.get("/ratings/pending/", {
        meta: { authOptional: false, suppressGlobalError: true },
      });
      const nextPending = response?.data || {};
      const sameEventPending =
        nextPending.has_pending &&
        Number(nextPending?.event?.id) === Number(event?.id);

      if (!sameEventPending) {
        showPeopleCompletion(dir);
        return;
      }

      const remainingPeople = mapPendingParticipants(nextPending);
      const nextPeopleFlow = nextPending?.ui?.people_flow || null;
      const eventRatingStillRequired = Boolean(
        nextPending?.pending?.require_event_rating &&
          !nextPending?.pending?.event_done
      );

      setPeopleFlow(nextPeopleFlow);

      if (nextPending?.ui?.completion_only) {
        setDirection(dir);
        setPhase("complete");
        return;
      }

      if (nextPeopleFlow?.decision_required) {
        setDirection(dir);
        setPhase("people_choice");
        return;
      }

      if (remainingPeople.length > 0) {
        const nextStageRequirements = getPendingStageRequirements(nextPending);
        setDirection(dir);
        setPeople(remainingPeople);
        setIdx(0);
        setForcedEvaluationMode(null);
        setCompletedModesByUserId({});
        setStageRequirements(nextStageRequirements);

        if (showGuideBeforePeople) {
          const nextIntroPages = buildRatingIntroPages({
            ...nextStageRequirements,
            isSportEvent: Boolean(nextPending?.event?.sport_type),
          });
          const guideStep = nextIntroPages.findIndex(
            (page) => page.kind === "guide" && page.mode === "social"
          );
          setGuideOnlyAfterChoice(true);
          setResumePhaseFromIntro("people");
          setIntroStep(Math.max(0, guideStep));
          setPhase("intro");
        } else {
          setGuideOnlyAfterChoice(false);
          setPhase("people");
        }
        return;
      }

      if (eventRatingStillRequired) {
        setDirection(dir);
        setPhase("event");
        setEventStep(EVENT_STEP_RATING);
        return;
      }

      showPeopleCompletion(dir);
    } catch (error) {
      console.error("Failed to reconcile pending evaluations", error);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              status: error?.response?.status,
              message:
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                t("common:something_went_wrong", "Something went wrong"),
            },
          })
        );
      }
    } finally {
      peopleActionInFlightRef.current = false;
      setPeopleActionSubmitting(false);
    }
  };

  const advanceAfterPerson = ({
    fromIndex,
    submission,
    dir = 0,
  }) => {
    const nextIndex = fromIndex + 1;
    setDirection(dir);
    setForcedEvaluationMode(null);

    if (nextIndex < people.length) {
      setIdx(nextIndex);
      return;
    }

    const nextPeopleFlow = submission?.people_flow || null;
    if (nextPeopleFlow?.decision_required) {
      setPeopleFlow(nextPeopleFlow);
      setPhase("people_choice");
      return;
    }
    if (
      peopleFlow?.stage === "optional" &&
      Number(nextPeopleFlow?.optional_remaining || 0) > 0
    ) {
      setPeopleFlow({
        ...nextPeopleFlow,
        decision_kind: "batch_complete",
      });
      setPhase("people_choice");
      return;
    }

    const transition = getPeopleCompletionTransition({
      submission,
      requiresEventRating,
    });

    if (transition === PEOPLE_COMPLETION_TRANSITIONS.EVENT) {
      setPhase("event");
      setEventStep(EVENT_STEP_RATING);
      return;
    }

    if (transition === PEOPLE_COMPLETION_TRANSITIONS.COMPLETE) {
      showPeopleCompletion(dir);
      return;
    }

    void reconcilePeopleQueue(dir);
  };

  const handleIntroPrimaryAction = () => {
    if (guideOnlyAfterChoice) {
      writeRatingIntroSession(
        getRatingSessionStorage(),
        evaluationIntroSessionKey,
        { introCompleted: true, introStep }
      );
      setGuideOnlyAfterChoice(false);
      setResumePhaseFromIntro(null);
      setForcedEvaluationMode(null);
      setPhase("people");
      return;
    }

    if (introStep < introPages.length - 1) {
      setIntroStep((prev) => {
        const nextStep = Math.min(introPages.length - 1, prev + 1);
        const storedIntroSession = readRatingIntroSession(
          getRatingSessionStorage(),
          evaluationIntroSessionKey
        );
        writeRatingIntroSession(
          getRatingSessionStorage(),
          evaluationIntroSessionKey,
          {
            introCompleted: storedIntroSession?.introCompleted === true,
            introStep: nextStep,
          }
        );
        return nextStep;
      });
      return;
    }
    writeRatingIntroSession(
      getRatingSessionStorage(),
      evaluationIntroSessionKey,
      { introCompleted: true, introStep }
    );
    setResumePhaseFromIntro(null);
    setForcedEvaluationMode(null);
    setPhase(
      resumePhaseFromIntro === "event"
        ? "event"
        : peopleFlow?.decision_required
        ? "people_choice"
        : "people"
    );
  };

  const handlePeopleChoice = async (action) => {
    if (!event?.id || peopleChoiceInFlightRef.current) return;
    peopleChoiceInFlightRef.current = true;
    setPeopleChoiceSubmitting(true);
    try {
      const response = await api.post("/ratings/pending/people-choice/", {
        event_id: event.id,
        action,
      });
      const nextPeopleFlow = response?.data?.people_flow || peopleFlow;

      if (action === "continue_optional") {
        await reconcilePeopleQueue(1, {
          showGuideBeforePeople:
            peopleFlow?.decision_kind === "deferred_return",
        });
        return;
      }

      setPeopleFlow(nextPeopleFlow);

      if (requiresEventRating) {
        setDirection(1);
        setPhase("event");
        setEventStep(EVENT_STEP_RATING);
        return;
      }

      showPeopleCompletion(1);
    } catch (error) {
      console.error("Failed to update optional people-rating choice", error);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              status: error?.response?.status,
              message:
                error?.response?.data?.error ||
                t("common:something_went_wrong", "Something went wrong"),
            },
          })
        );
      }
    } finally {
      peopleChoiceInFlightRef.current = false;
      setPeopleChoiceSubmitting(false);
    }
  };

  const completeCurrentMode = ({
    person = current,
    personIndex = idx,
    progress = currentProgress,
    mode,
    submission,
    dir = 0,
  }) => {
    const userId = getCurrentUserId(person);
    const nextProgress = {
      socialDone: progress.socialDone || mode === "social",
      skillDone: progress.skillDone || mode === "skill",
    };

    if (userId) {
      setCompletedModesByUserId((prev) => ({
        ...prev,
        [userId]: {
          ...(prev[userId] || {}),
          ...nextProgress,
        },
      }));
    }

    const hasRemainingSocial = requireSocial && !nextProgress.socialDone;
    const hasRemainingSkill = requireSkill && !nextProgress.skillDone;
    if (hasRemainingSocial || hasRemainingSkill) {
      setDirection(0);
      setForcedEvaluationMode(null);
      return;
    }

    advanceAfterPerson({ fromIndex: personIndex, submission, dir });
  };

  const completeCurrentPerson = ({
    person = current,
    personIndex = idx,
    submission,
    dir = 0,
  } = {}) => {
    const userId = getCurrentUserId(person);

    if (userId) {
      setCompletedModesByUserId((prev) => ({
        ...prev,
        [userId]: {
          ...(prev[userId] || {}),
          socialDone: true,
          skillDone: true,
        },
      }));
    }

    advanceAfterPerson({ fromIndex: personIndex, submission, dir });
  };

  const onAction = async (type, dir = 0) => {
    if (phase !== "people") return;
    if (!current) return;
    if (peopleActionSubmitting || peopleActionInFlightRef.current) return;

    const actionPerson = current;
    const actionPersonIndex = idx;
    const actionMode = currentEvaluationMode;
    const actionProgress = currentProgress;
    const evaluatedId =
      actionPerson.user_id || actionPerson.userId || actionPerson.id || null;

    // MARK (избранное через StorageFavourite)
    if (type === "mark") {
      if (!evaluatedId) return;

      const storageId = current.storage_id || current.storageId || null;
      const alreadyMarked = !!markedById[evaluatedId];
      const nextMarked = !alreadyMarked;

      // Оптимистично обновляем UI
      setMarkedById((prev) => ({ ...prev, [evaluatedId]: nextMarked }));

      // Если нет storageId (например, dummy данные) — только локальный toggle
      if (!storageId || !event?.id) {
        return;
      }

      try {
        if (nextMarked) {
          await api.post(`/storage/add-to-favourites/${storageId}/`);
        } else {
          await api.post(`/storage/remove-from-favourites/${storageId}/`);
        }
      } catch (error) {
        console.error("Failed to toggle favourite from rating modal", error);
        // Откатываем локальный стейт, если запрос не удался
        setMarkedById((prev) => ({ ...prev, [evaluatedId]: alreadyMarked }));
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("global-error", {
              detail: {
                status: error?.response?.status,
                message:
                  error?.response?.data?.error ||
                  t("common:something_went_wrong", "Something went wrong"),
              },
            })
          );
        }
      }
      return;
    }

    // Открыть форму репорта пользователя
    if (type === "report") {
      setReportMode("user");
      setPeopleReportStep("reasons");
      setPeopleReportReason("");
      setPeopleReportComment("");
      setPeopleReportError("");
      setPeopleReportOpen(true);
      return;
    }

    if (type === "photo_bad") {
      if (!event?.id || !evaluatedId) {
        completeCurrentPerson({
          person: actionPerson,
          personIndex: actionPersonIndex,
          dir,
        });
        return;
      }

      const payload = { event_id: event.id };
      const shouldSubmitSocialPhotoBad =
        actionMode === "social" ||
        (requireSocial && !actionProgress.socialDone);
      const shouldSubmitSkillSkip =
        actionMode === "skill" ||
        (requireSkill && !actionProgress.skillDone);

      if (shouldSubmitSocialPhotoBad) {
        payload.social = [
          {
            evaluated_id: evaluatedId,
            valuation: "photo_bad",
          },
        ];
      }

      if (shouldSubmitSkillSkip) {
        payload.skill = [
          {
            evaluated_id: evaluatedId,
            valuation: 0,
          },
        ];
      }

      const submission = await submitPendingPeopleRating(
        payload,
        "Failed to submit bad photo valuation"
      );
      if (!submission) return;
      if (shouldSubmitSocialPhotoBad) {
        notifyVaultConnectionsChanged();
      }

      completeCurrentPerson({
        person: actionPerson,
        personIndex: actionPersonIndex,
        submission,
        dir,
      });
      return;
    }

    if (type === "not_interacted") {
      if (!event?.id || !evaluatedId) {
        completeCurrentMode({
          person: actionPerson,
          personIndex: actionPersonIndex,
          progress: actionProgress,
          mode: "social",
          dir,
        });
        return;
      }

      const submission = await submitPendingPeopleRating(
        {
          event_id: event.id,
          action: "neutral_optional_user",
          evaluated_id: evaluatedId,
        },
        "Failed to mark optional attendee as not interacted",
        "/ratings/pending/people-choice/"
      );
      if (!submission) return;
      setPeopleFlow(submission.people_flow || peopleFlow);
      completeCurrentMode({
        person: actionPerson,
        personIndex: actionPersonIndex,
        progress: actionProgress,
        mode: "social",
        submission,
        dir,
      });
      return;
    }

    // Если нет id события — просто листаем
    if (!event?.id || !evaluatedId) {
      completeCurrentMode({
        person: actionPerson,
        personIndex: actionPersonIndex,
        progress: actionProgress,
        mode: actionMode,
        dir,
      });
      return;
    }

    // Оценка скилла (спорт)
    if (
      type === "skill_level_1" ||
      type === "skill_level_2" ||
      type === "skill_level_3"
    ) {
      const valuation =
        type === "skill_level_1" ? 1 : type === "skill_level_2" ? 2 : 3;

      const submission = await submitPendingPeopleRating(
        {
          event_id: event.id,
          skill: [
            {
              evaluated_id: evaluatedId,
              valuation,
            },
          ],
        },
        "Failed to submit skill valuation"
      );
      if (!submission) return;

      completeCurrentMode({
        person: actionPerson,
        personIndex: actionPersonIndex,
        progress: actionProgress,
        mode: "skill",
        submission,
        dir,
      });
      return;
    }

    if (type === "skip" && actionMode === "skill") {
      const submission = await submitPendingPeopleRating(
        {
          event_id: event.id,
          skill: [
            {
              evaluated_id: evaluatedId,
              valuation: 0,
            },
          ],
        },
        "Failed to submit skipped skill valuation"
      );
      if (!submission) return;

      completeCurrentMode({
        person: actionPerson,
        personIndex: actionPersonIndex,
        progress: actionProgress,
        mode: "skill",
        submission,
        dir,
      });
      return;
    }

    const valuation = SOCIAL_ACTION_VALUATIONS[type];

    if (!valuation) {
      completeCurrentMode({
        person: actionPerson,
        personIndex: actionPersonIndex,
        progress: actionProgress,
        mode: actionMode,
        dir,
      });
      return;
    }

    const submission = await submitPendingPeopleRating(
      {
        event_id: event.id,
        social: [
          {
            evaluated_id: evaluatedId,
            valuation,
            ...(type === "discomfort"
              ? { report_kind: "discomfort" }
              : {}),
          },
        ],
      },
      "Failed to submit social valuation"
    );
    if (!submission) return;
    notifyVaultConnectionsChanged();

    if (type === "discomfort" && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: { message: t("people.discomfort_sent") },
        })
      );
    }

    completeCurrentMode({
      person: actionPerson,
      personIndex: actionPersonIndex,
      progress: actionProgress,
      mode: "social",
      submission,
      dir,
    });
  };

  // Handles report submission to backend
  const handleReportSubmit = async (message, modeOverride = reportMode) => {
    if (modeOverride === "user") {
      const reportedPerson = current;
      const reportedPersonIndex = idx;
      const storageCode = reportedPerson?.code;
      if (!storageCode) {
        console.warn("No code for current card, skipping report");
        return;
      }
      const evaluatedId =
        reportedPerson?.user_id ||
        reportedPerson?.userId ||
        reportedPerson?.id ||
        null;
      let ratingSubmission = null;

      try {
        await api.post(
          "/accounts/report-user/",
          {
            code: storageCode,
            description: message,
            report_kind: "formal_report",
            event_id: event?.id || null,
          },
          {
            headers: {
              "X-CSRFToken": window.getCookie("csrftoken"),
            },
          }
        );

        if (event?.id && evaluatedId) {
          const payload = { event_id: event.id };
          const shouldSubmitSocialReport =
            currentEvaluationMode === "social" ||
            (requireSocial && !currentProgress.socialDone);
          const shouldSubmitSkillSkip =
            currentEvaluationMode === "skill" ||
            (requireSkill && !currentProgress.skillDone);

          if (shouldSubmitSocialReport) {
            payload.social = [
              {
                evaluated_id: evaluatedId,
                valuation: "reported",
                report_kind: "formal_report",
              },
            ];
          }

          if (shouldSubmitSkillSkip) {
            payload.skill = [
              {
                evaluated_id: evaluatedId,
                valuation: 0,
              },
            ];
          }

          try {
            if (payload.social || payload.skill) {
              const ratingResponse = await api.post(
                "/ratings/pending/submit/",
                payload
              );
              ratingSubmission = ratingResponse?.data || {};
              if (payload.social) {
                notifyVaultConnectionsChanged();
              }
            }
          } catch (err) {
            console.error("Failed to submit rating valuation after report", err);
            throw err;
          }
        } else {
          console.warn(
            "Cannot submit reported valuation - missing event.id or evaluatedId",
            { event, evaluatedId }
          );
        }

        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t("report_form.success"),
            },
          })
        );

        setIsReportOpen(false);
        setPeopleReportOpen(false);
        setPeopleReportStep("reasons");
        setPeopleReportReason("");
        setPeopleReportComment("");
        setPeopleReportError("");
        if (phase === "people") {
          completeCurrentPerson({
            person: reportedPerson,
            personIndex: reportedPersonIndex,
            submission: ratingSubmission,
          });
        }
      } catch (e) {
        const errorMessage =
          e.response?.data?.error || t("report_form.error");
        setPeopleReportError(errorMessage);
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              status: e.response?.status,
              message: errorMessage,
            },
          })
        );
        throw e;
      }
    } else if (modeOverride === "event") {
      if (!event?.id) {
        console.warn("No event id for event report, skipping");
        return;
      }

      try {
        await api.post(
          "/events/report-event/",
          {
            event_id: event.id,
            description: message,
          },
          {
            headers: {
              "X-CSRFToken": window.getCookie("csrftoken"),
            },
          }
        );

        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t("report_form.success"),
            },
          })
        );

        setIsReportOpen(false);
      } catch (e) {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              status: e.response?.status,
              message:
                e.response?.data?.error || t("report_form.error"),
            },
          })
        );
        throw e;
      }
    }
  };

  const handlePeopleReportSubmit = async () => {
    if (
      !peopleReportReason ||
      peopleReportSubmitting ||
      peopleReportInFlightRef.current
    ) {
      return;
    }

    const reasonLabel = t(`people.report.reasons.${peopleReportReason}`);
    const trimmedComment = peopleReportComment.trim();
    const message = trimmedComment
      ? `${reasonLabel}\n\n${trimmedComment}`
      : reasonLabel;

    try {
      peopleReportInFlightRef.current = true;
      setPeopleReportSubmitting(true);
      setPeopleReportError("");
      await handleReportSubmit(message, "user");
    } catch (error) {
      console.error("Failed to submit people report", error);
    } finally {
      peopleReportInFlightRef.current = false;
      setPeopleReportSubmitting(false);
    }
  };

  const handleEventRatingSubmit = async (unregisteredAttendeesOverride = undefined) => {
    if (eventRatingSubmitting || eventRatingInFlightRef.current) return;

    if (!event?.id) {
      console.log("Submit event rating (no backend event)", {
        rating: eventRating,
        event,
      });
      // даже если нет ивента — можно просто закрыть модалку
      setOpen(false);
      return;
    }

    const resolvedSawUnregisteredAttendees =
      typeof unregisteredAttendeesOverride === "boolean"
        ? unregisteredAttendeesOverride
        : sawUnregisteredAttendees;

    eventRatingInFlightRef.current = true;
    setEventRatingSubmitting(true);
    try {
      const response = await api.post("/ratings/pending/submit/", {
        event_id: event.id,
        event_rating: {
          valuation: eventRating,
          anonymous_host_feedback: hostFeedbackEnabled
            ? hostFeedbackText.trim()
            : "",
          saw_unregistered_attendees: shouldAskUnregisteredAttendees
            ? resolvedSawUnregisteredAttendees
            : null,
        },
      });

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t("people.event_rated_success", "Event rating saved"),
            },
          })
        );
      }

      const nextEventReward = response?.data?.event_reward;
      if (nextEventReward?.reward) {
        // Keep the modal open until the user acknowledges the reward. The
        // backend has already credited it atomically, so a lost response can
        // safely be replayed and will return the same claim.
        setEventReward(nextEventReward);
        setEventStep(EVENT_STEP_REWARD);
        return;
      }

      // Keep the flow explicit: the task is already saved, but the modal only
      // closes after the user acknowledges the completion screen.
      showPeopleCompletion(1);
    } catch (error) {
      console.error("Failed to submit event rating", error);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              status: error?.response?.status,
              message:
                error?.response?.data?.error ||
                t("common:something_went_wrong", "Something went wrong"),
            },
          })
        );
      }
    } finally {
      eventRatingInFlightRef.current = false;
      setEventRatingSubmitting(false);
    }
  };

  const handleEventPrimaryAction = () => {
    if (shouldAskUnregisteredAttendees && eventStep === EVENT_STEP_RATING) {
      setEventStep(EVENT_STEP_ATTENDANCE);
      return;
    }
    void handleEventRatingSubmit();
  };

  const handleUnregisteredAttendeesAnswer = (value) => {
    if (eventRatingSubmitting) return;
    if (value) {
      setSawUnregisteredAttendees(true);
      setEventStep(EVENT_STEP_ATTENDANCE_CONFIRM);
      return;
    }
    setSawUnregisteredAttendees(value);
    void handleEventRatingSubmit(value);
  };

  const handleUnregisteredAttendeesConfirm = () => {
    if (eventRatingSubmitting || eventRatingInFlightRef.current) return;
    setSawUnregisteredAttendees(true);
    void handleEventRatingSubmit(true);
  };

  const finishEventReward = async () => {
    if (eventRewardClosing) return;
    setEventRewardClosing(true);
    try {
      clearRatingIntroSession(
        getRatingSessionStorage(),
        evaluationIntroSessionKey
      );
      await refresh?.();
      await refreshRequiredActions?.({ silent: true });
      setOpen(false);
    } finally {
      setEventRewardClosing(false);
    }
  };

  const finishPeopleCompletion = async () => {
    if (completionClosingRef.current) return;
    completionClosingRef.current = true;
    setCompletionClosing(true);
    try {
      if (pending?.ui?.completion_only && event?.id) {
        await api.post("/ratings/pending/people-choice/", {
          event_id: event.id,
          action: "acknowledge_completion",
        });
      }
      clearRatingIntroSession(
        getRatingSessionStorage(),
        evaluationIntroSessionKey
      );
      setOpen(false);
      await refresh?.();
      await refreshRequiredActions?.({ silent: true });
    } finally {
      completionClosingRef.current = false;
      setCompletionClosing(false);
    }
  };

  const handleCloseClick = () => {
    if (
      peopleActionInFlightRef.current ||
      peopleReportInFlightRef.current ||
      eventRatingInFlightRef.current ||
      completionClosingRef.current ||
      peopleActionSubmitting ||
      peopleReportSubmitting ||
      peopleChoiceSubmitting ||
      eventRatingSubmitting ||
      eventRewardClosing
    ) {
      return;
    }
    if (phase === "complete") {
      void finishPeopleCompletion();
      return;
    }
    if (eventStep === EVENT_STEP_REWARD) {
      void finishEventReward();
      return;
    }
    setOpen(false);
  };

  const root =
    typeof document !== "undefined"
      ? document.getElementById("modal-root") || document.body
      : null;

  if (!root) return null;

  if (!isVisible) {
    return createPortal(<AnimatePresence>{null}</AnimatePresence>, root);
  }

  const eventPrimaryButtonLabel = t("people.finish_button");
  const eventRewardItems = getEventRewardItems(eventReward?.reward);

  const codeMarkClassName = cn(
    "vg-rating-code-mark",
    isPremiumTheme && "vg-rating-code-mark--premium"
  );

  const avatarFrameClassName = cn(
    "absolute left-1/2 -translate-x-1/2 top-[116px] z-[2] w-[210px] h-[210px] rounded-full border border-gray-700 overflow-hidden",
    isPremiumTheme && "vg-rating-avatar-frame--premium"
  );
  const selfPreviewAvatarFrameClassName = cn(
    "absolute left-1/2 -translate-x-1/2 top-[156px] z-[2] w-[224px] h-[224px] rounded-full border border-gray-700 overflow-hidden",
    isPremiumTheme && "vg-rating-avatar-frame--premium"
  );

  const actionLabelClassName = cn(
    "text-xs font-medium",
    isPremiumTheme ? "vg-rating-action-label--premium" : "text-white/60"
  );

  const getActionSurfaceClassName = (variant, size = "sm") =>
    cn(
      size === "lg" ? "w-16 h-16" : "w-14 h-14",
      "rounded-full flex items-center justify-center vg-rating-action-surface",
      isPremiumTheme
        ? cn(
            "vg-rating-action-surface--premium",
            variant === "primary" && "vg-rating-action-surface--premium-primary",
            variant === "secondary" && "vg-rating-action-surface--premium-secondary",
            variant === "active" && "vg-rating-action-surface--premium-active",
            (variant === "muted" || !variant) && "vg-rating-action-surface--premium-muted"
          )
        : "border border-white/70 bg-black/30 backdrop-blur-md"
    );

  const skillButtonClassName = cn(
    "w-[180px] h-8 rounded-full flex items-center justify-center px-4 vg-rating-skill-pill",
    isPremiumTheme
      ? "vg-rating-skill-pill--premium"
      : "border border-white/80 bg-black/40 backdrop-blur-md"
  );
  const premiumUserPillClassName = cn(
    "vg-rating-person-status vg-rating-pill",
    isPremiumTheme
      ? "vg-rating-pill--premium vg-rating-pill--premium-quiet"
      : "border border-white/70 bg-black/30 backdrop-blur-md"
  );
  const personCodeClassName = cn(
    "vg-rating-person-code vg-rating-identity-chip",
    isPremiumTheme ? "vg-rating-identity-chip--premium" : "vg-rating-identity-chip--standard"
  );
  const personSafetyPillClassName = cn(
    "vg-rating-pill",
    isPremiumTheme
      ? "vg-rating-pill--premium vg-rating-pill--premium-quiet"
      : "border border-white/70 bg-black/35 backdrop-blur-md"
  );
  const isPeopleReportMode = phase === "people" && peopleReportOpen;
  const isPeopleReportDetailsStep =
    isPeopleReportMode && peopleReportStep === "details";
  const peopleReportReasonOptions = PEOPLE_REPORT_REASONS.map((reason) => ({
    value: reason,
    label: t(`people.report.reasons.${reason}`),
  }));
  const openPremiumPage = () => {
    const params = new URLSearchParams({
      src: "event_rating",
      feature: "premium_user_card",
      tier: "premium",
      from: `${location.pathname}${location.search}`,
    });
    navigate(`/menu/subscription?${params.toString()}`);
  };

  const renderRatingEventCard = (titleMaxWidthClass = "max-w-[190px]") => (
    <EventCard
      title={event.title}
      avatar={event.imageUrl || null}
      isOnline={false}
      isPersonal={true}
      isPaid={false}
      minVaultLevel={event.min_vault_level}
      datetime={event.dateTime}
      isVerified={false}
      isValidated={true}
      color="#ffffff"
      isFavourite={false}
      onToggleFavourite={() => {}}
      isSelected={false}
      fromMap={false}
      status={"active"}
      badgeLabel={event.badgeLabelId}
      isPromoted={false}
      isMainEvent={false}
      unreadCount={0}
      type={"event"}
      titleMaxWidthClass={titleMaxWidthClass}
      showFavouriteButton={false}
      searchListLayout
      cardClassName="bg-white/[0.06] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
    />
  );
  const introCopyKey = activeIntroMode === "skill" ? "sport" : "networking";
  const ratingModalTitle =
    isPeopleReportMode
      ? t("people.report.title")
      : phase === "intro" && activeIntroKind === "calibration"
      ? calibrationCopy.title
      : phase === "event" && eventStep === EVENT_STEP_REWARD
      ? t("reward.title")
      : phase === "people_choice"
      ? t("people_flow.title")
      : phase === "complete"
      ? t("completion.title")
      : t("modal.title");
  const introFeatureItems = [
    {
      key: "preferences",
      Icon: PreferenceIntroIcon,
      iconClassName: "text-[#A993FF]",
      title: t(`modal.intro.${introCopyKey}.preference_card_title`),
      body: t(`modal.intro.${introCopyKey}.preference_card_body`),
    },
    {
      key: "safety",
      Icon: SafetyIntroIcon,
      iconClassName: "text-[#F06FC8]",
      title: t(`modal.intro.${introCopyKey}.safety_card_title`),
      body: t(`modal.intro.${introCopyKey}.safety_card_body`),
    },
  ];
  const socialGuideActions = [
    {
      key: "like",
      label: t("modal.actions.like.label"),
      description: t("modal.actions.like.description"),
      iconSrc: "/assets/rating/social_my_vibe_v3.svg",
      iconClassName: "h-6 w-6",
      surfaceClassName: "border-[#D7CCFF]/55 bg-[#A993FF]/16",
    },
    {
      key: "next",
      label: t("modal.actions.next.label"),
      description: t("modal.actions.next.description"),
      iconSrc: "/assets/rating/social_not_my_vibe_v3.svg",
      iconClassName: "h-6 w-6",
      surfaceClassName: "border-[#AFC7FF]/55 bg-[#7BA7FF]/14",
    },
    {
      key: "skip",
      label: t("modal.actions.skip.label"),
      description: t("modal.actions.skip.description"),
      iconSrc: "/assets/rating/skip_icon.svg",
      iconClassName: "h-6 w-6",
      surfaceClassName: "border-white/32 bg-white/[0.055]",
    },
    {
      key: "discomfort",
      label: t("modal.actions.discomfort.label"),
      description: t("modal.actions.discomfort.description"),
      iconSrc: "/assets/rating/social_discomfort_v3.svg",
      iconClassName: "h-6 w-6",
      surfaceClassName: "border-[#FFD6F1]/48 bg-[#F06FC8]/12",
    },
  ];
  const sportLevelGuideItems = [
    {
      key: "beginner",
      label: t("modal.sport_levels.beginner.label"),
      description: t("modal.sport_levels.beginner.description"),
      iconSrc: "/assets/events/beginner.svg",
      iconClassName: "h-8 w-8",
      surfaceClassName: "border-[#BFD0FF]/48 bg-[#9EB9FF]/12",
      compact: true,
    },
    {
      key: "intermediate",
      label: t("modal.sport_levels.intermediate.label"),
      description: t("modal.sport_levels.intermediate.description"),
      iconSrc: "/assets/events/intermediate.svg",
      iconClassName: "h-8 w-8",
      surfaceClassName: "border-[#D8C4FF]/48 bg-[#B68CFF]/12",
      compact: true,
    },
    {
      key: "advanced",
      label: t("modal.sport_levels.advanced.label"),
      description: t("modal.sport_levels.advanced.description"),
      iconSrc: "/assets/events/advanced.svg",
      iconClassName: "h-8 w-8",
      surfaceClassName: "border-[#FFE0A6]/50 bg-[#FFB84A]/14",
      compact: true,
    },
  ];
  const renderGuideActionItem = ({
    key,
    label,
    description,
    iconSrc,
    iconClassName,
    surfaceClassName,
    compact = false,
  }) => (
    <div
      key={key}
      className={cn(
        "flex w-full items-center gap-3 border border-white/12 bg-white/[0.05] shadow-[inset_0_1px_0_rgba(255,255,255,0.055)]",
        compact
          ? "min-h-[64px] rounded-[18px] px-3 py-2"
          : "min-h-[72px] rounded-[20px] px-3 py-2"
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full border backdrop-blur-md",
          compact ? "h-[50px] w-[50px]" : "h-[58px] w-[58px]",
          surfaceClassName
        )}
      >
        <img src={iconSrc} alt="" className={iconClassName} draggable={false} />
      </div>
      <div className="min-w-0 text-left">
        <p
          className={cn(
            "font-semibold leading-tight text-white",
            compact ? "text-[14px]" : "text-[15px]"
          )}
        >
          {label}
        </p>
        <p
          className={cn(
            "mt-1 text-[12px] font-medium text-white/68",
            compact ? "leading-[1.22]" : "leading-[1.28]"
          )}
        >
          {description}
        </p>
      </div>
    </div>
  );

  const modalBody = (
    <RatingCardMotion
      active={isVisible && (phase === "people" || isSelfPreview) && !isReportOpen && !peopleReportOpen && !peopleActionSubmitting}
      className={cn(
        "vg-rating-card-shell",
        phase !== "people" && !isSelfPreview && "vg-rating-card-shell--standard",
        isPremiumTheme && "vg-rating-card-shell--premium"
      )}
      style={premiumCardThemeStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <RatingCardSurface plain={phase !== "people" && !isSelfPreview}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={
              phase === "people"
                ? `people-${current?.id ?? idx}`
                : phase === "intro"
                ? `intro-${introStep}`
                : phase === "people_choice"
                ? `people-choice-${peopleFlow?.decision_kind || "choice"}`
                : phase === "event"
                ? `event-${eventStep}`
                : phase
            }
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            className="absolute inset-0"
          >
            {/* Card previews use the selected Vault; instructional screens use the shared gradient. */}
            {phase === "people" || isSelfPreview ? (
              <>
                <img
                  src={displayedCardPerson?.castleBgUrl}
                  alt=""
                  onError={(event) => {
                    applyBundledCastleSkinFallback(event.currentTarget);
                  }}
                  className={cn(
                    "vg-card-castle-background",
                    hasPremiumCardAccess
                      ? "blur-[1.5px] saturate-[1.16]"
                      : "blur-[3px]"
                  )}
                  draggable={false}
                />
                <div className={cn("absolute inset-0", isPremiumTheme ? "bg-black/34" : "bg-black/40")} />
                {isPremiumTheme && (
                  <div className="absolute inset-0 vg-rating-card-overlay--premium" />
                )}
              </>
            ) : (
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(51,63,78,0.94)_0%,rgba(35,48,62,0.95)_48%,rgba(23,54,70,0.96)_100%)]" />
            )}

            <motion.header
              variants={appModalContentItemVariants}
              className="absolute left-0 top-0 z-20 w-full px-5 pt-4"
            >
              {(isPeopleReportMode || canGoBack) && (
                <AppModalIconButton
                  onClick={handleBack}
                  aria-label={t("people.back_button")}
                  className="absolute left-5 top-4"
                  disabled={
                    peopleActionSubmitting ||
                    peopleReportSubmitting ||
                    eventRatingSubmitting
                  }
                >
                  <ModalBackIcon />
                </AppModalIconButton>
              )}

              <motion.h1
                variants={appModalContentItemVariants}
                className={cn(
                  "mx-auto flex h-11 max-w-[220px] items-center justify-center whitespace-nowrap text-center text-[30px] font-semibold leading-none tracking-tight text-white",
                  "vg-rating-card-title",
                  isPremiumTheme && "vg-rating-card-title--premium"
                )}
              >
                {ratingModalTitle}
              </motion.h1>

              <AppModalIconButton
                onClick={handleCloseClick}
                aria-label={t("modal.close_button")}
                className="absolute right-5 top-4"
                disabled={
                  peopleActionSubmitting ||
                  peopleReportSubmitting ||
                  eventRatingSubmitting ||
                  peopleChoiceSubmitting ||
                  eventRewardClosing ||
                  completionClosing
                }
              >
                <ModalCloseIcon />
              </AppModalIconButton>
            </motion.header>

            <div className="relative w-full h-full">
              {phase === "intro" ? (
              // ---------- INTRO PHASE ----------
                <div className="absolute inset-0 flex flex-col items-center px-7 pb-5 pt-[76px] text-white">
                  {activeIntroKind === "self_preview" ? (
                    <>
                      <div className="absolute left-0 top-[82px] z-[4] flex w-full justify-center px-6">
                        <div className={codeMarkClassName}>
                          {selfPreviewPerson?.code}
                        </div>
                      </div>

                      <div className={selfPreviewAvatarFrameClassName}>
                        <img
                          src={selfPreviewPerson?.avatarUrl}
                          alt=""
                          className="h-full w-full object-cover"
                          draggable={false}
                        />
                      </div>

                      <div className="absolute inset-x-[18px] bottom-[82px] z-[4] flex min-h-[96px] flex-col items-center justify-center rounded-[26px] border border-white/55 bg-black/35 px-5 py-3 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.10)] backdrop-blur-md">
                        <p className="text-[16px] font-semibold leading-tight text-white">
                          {t("modal.intro.self_preview.title")}
                        </p>
                        <p className="mt-2 text-[13px] font-medium leading-[1.35] text-white/76">
                          {t("modal.intro.self_preview.body")}
                        </p>
                      </div>
                    </>
                  ) : activeIntroKind === "calibration" ? (
                    <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-start pb-0 pt-[24px]">
                      <SocialCalibrationContent
                        user={calibrationUser}
                        topText={calibrationCopy.topText}
                        bottomText={calibrationCopy.bottomText}
                        visualClassName="!w-[236px]"
                        topTextClassName="!text-[14px] !font-semibold !leading-[1.45] !text-white/88"
                        bottomTextClassName="!text-[14px] !font-semibold !leading-[1.45] !text-white/82"
                      />
                    </div>
                  ) : activeIntroKind === "event" ? (
                    <div className="flex min-h-0 w-full flex-1 flex-col items-center">
                      <div className="w-full max-w-[338px] px-[4px]">
                        {renderRatingEventCard()}
                      </div>

                      <div className="mt-6 w-full max-w-[330px] text-center">
                        <p className="text-[23px] font-semibold leading-tight text-white">
                          {t(`modal.intro.${introCopyKey}.system_title`)}
                        </p>
                        <p className="mt-3 text-[14px] font-medium leading-[1.45] text-white/72">
                          {t(`modal.intro.${introCopyKey}.system_body`)}
                        </p>
                      </div>

                      <div className="mt-5 flex w-full max-w-[330px] flex-col gap-3">
                        {introFeatureItems.map(({ key, Icon, iconClassName, title, body }) => (
                          <div
                            key={key}
                            className="flex min-h-[82px] items-center gap-3 rounded-[20px] border border-white/12 bg-white/[0.045] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-md"
                          >
                            <div
                              className={cn(
                                "flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border border-current bg-black/[0.18]",
                                iconClassName
                              )}
                            >
                              <Icon />
                            </div>
                            <div className="min-w-0 text-left">
                              <p className="text-[15px] font-semibold leading-tight text-white">
                                {title}
                              </p>
                              <p className="mt-1 text-[13px] font-medium leading-snug text-white/68">
                                {body}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : activeIntroMode === "skill" ? (
                    <div className="flex min-h-0 w-full flex-1 flex-col items-center">
                      <div className="w-full max-w-[314px] text-center">
                        <p className="text-[20px] font-semibold leading-tight text-white">
                          {t("people.skill_stage")}
                        </p>
                        <p className="mt-2 text-[12px] font-medium leading-[1.35] text-white/58">
                          {t("modal.intro.sport.guide_hint")}
                        </p>
                      </div>

                      <div className="mt-4 flex w-full max-w-[314px] flex-col gap-2">
                        {sportLevelGuideItems.map(renderGuideActionItem)}
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-h-0 w-full flex-1 flex-col items-center">
                      <div className="w-full max-w-[314px] text-center">
                        <p className="text-[20px] font-semibold leading-tight text-white">
                          {t("people.social_stage")}
                        </p>
                      </div>

                      <div className="mt-4 flex w-full max-w-[314px] flex-col gap-2">
                        {socialGuideActions.map(renderGuideActionItem)}
                      </div>
                    </div>
                  )}

                  <motion.button
                    type="button"
                    onClick={handleIntroPrimaryAction}
                    className={cn(
                      "mt-auto flex items-center justify-center rounded-full bg-white px-5 font-semibold leading-none text-[#19233A] shadow-[0_10px_28px_rgba(0,0,0,0.18)] transition active:scale-[0.985]",
                      activeIntroKind === "event" ||
                      activeIntroKind === "calibration" ||
                      activeIntroKind === "self_preview"
                        ? "h-12 w-full max-w-[330px] text-[16px]"
                        : "h-10 w-[126px] text-[15px]"
                    )}
                    {...TAP_FEEDBACK}
                  >
                    {activeIntroKind === "self_preview"
                      ? t("modal.intro.self_preview.continue_button")
                      : guideOnlyAfterChoice
                      ? t("modal.start_button")
                      : introStep < introPages.length - 1
                      ? t("modal.next_button")
                      : t("modal.start_button")}
                  </motion.button>
                  {activeIntroKind === "guide" ? (
                    <p className="mt-2 text-[10px] text-white/60 text-center leading-snug">
                      {t("modal.footer_private")}
                    </p>
                  ) : null}
                </div>
              ) : phase === "people_choice" ? (
                <div className="absolute inset-0 flex flex-col items-center px-7 pb-6 pt-[82px] text-center text-white">
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.28, ease: "easeOut" }}
                    className="w-full max-w-[330px]"
                  >
                    {renderRatingEventCard("max-w-[210px]")}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.28 }}
                    className="mt-5 w-full max-w-[322px]"
                  >
                    <h2 className="text-[24px] font-semibold leading-tight text-white">
                      {t(`people_flow.${peopleFlow?.decision_kind || "contacts_complete"}.heading`)}
                    </h2>
                    <p className="mt-3 text-[14px] font-medium leading-[1.5] text-white/76">
                      {t(`people_flow.${peopleFlow?.decision_kind || "contacts_complete"}.body`, {
                        count: peopleFlow?.optional_remaining || peopleFlow?.optional_total || 0,
                        batchSize: peopleFlow?.batch_size || 10,
                      })}
                    </p>
                  </motion.div>

                  <div className="mt-auto flex w-full max-w-[330px] flex-col gap-2.5">
                    <AppModalButton
                      onClick={() => handlePeopleChoice("continue_optional")}
                      disabled={peopleChoiceSubmitting}
                      loading={peopleChoiceSubmitting}
                    >
                      {peopleFlow?.decision_kind === "batch_complete"
                        ? t("people_flow.next_batch")
                        : ["large_event", "deferred_return"].includes(
                            peopleFlow?.decision_kind
                          )
                        ? t("people_flow.start_review")
                        : t("people_flow.evaluate_now")}
                    </AppModalButton>
                    {peopleFlow?.decision_kind !== "deferred_return" &&
                    peopleFlow?.can_defer_optional !== false ? (
                      <AppModalButton
                        variant="secondary"
                        onClick={() => handlePeopleChoice("defer_optional")}
                        disabled={peopleChoiceSubmitting}
                      >
                        {t("people_flow.ask_tomorrow")}
                      </AppModalButton>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handlePeopleChoice("decline_optional")}
                      disabled={peopleChoiceSubmitting}
                      className="h-9 text-[13px] font-semibold text-white/58 transition hover:text-white/82 disabled:opacity-45"
                    >
                      {peopleFlow?.decision_kind === "batch_complete"
                        ? t("people_flow.finish_people")
                        : t("people_flow.dont_rate")}
                    </button>
                  </div>
                </div>
              ) : phase === "complete" ? (
                <div className="absolute inset-0 flex flex-col items-center px-7 pb-6 pt-[92px] text-center text-white">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.72 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.28, ease: "easeOut" }}
                    className="flex h-[112px] w-[112px] items-center justify-center rounded-full border border-[#7DE2C5]/60 bg-[#7DE2C5]/12 text-[#A7F3DE] shadow-[0_0_44px_rgba(125,226,197,0.18)]"
                  >
                    <CompletionCheckIcon />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12, duration: 0.28 }}
                    className="mt-8 w-full max-w-[320px]"
                  >
                    <h2 className="text-[26px] font-semibold leading-tight text-white">
                      {viewerIsHost
                        ? t("completion.host_heading")
                        : t("completion.participant_heading")}
                    </h2>
                    <p className="mt-4 text-[15px] font-medium leading-[1.55] text-white/76">
                      {viewerIsHost
                        ? t("completion.host_body")
                        : t("completion.participant_body")}
                    </p>
                  </motion.div>

                  <div className="mt-auto w-full max-w-[330px]">
                    <AppModalButton
                      onClick={finishPeopleCompletion}
                      disabled={completionClosing}
                      loading={completionClosing}
                    >
                      {t("completion.button")}
                    </AppModalButton>
                  </div>
                </div>
              ) : phase === "event" ? (
              // ---------- EVENT RATING PHASE ----------
                <div
                  className={cn(
                    "absolute inset-0 px-5 pb-6 pt-[72px] text-white",
                    eventStep === EVENT_STEP_ATTENDANCE ||
                    eventStep === EVENT_STEP_ATTENDANCE_CONFIRM
                      ? "overflow-hidden"
                      : "overflow-y-auto"
                  )}
                >
                  {eventStep === EVENT_STEP_REWARD ? (
                    <div className="relative mx-auto flex min-h-full w-full max-w-[330px] flex-col items-center pb-2 text-center">
                      <RewardConfetti />
                      <div className="relative mt-[-2px] flex h-[92px] w-full items-center justify-center">
                        <RewardCelebrationIcon />
                      </div>

                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.24, duration: 0.28 }}
                        className="relative z-[1] mt-2"
                      >
                        <h2 className="text-[24px] font-semibold leading-tight text-white">
                          {t("reward.heading")}
                        </h2>
                        <p className="mt-2 text-[14px] font-medium leading-[1.45] text-white/74">
                          {t("reward.body")}
                        </p>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.38, duration: 0.3 }}
                        className={cn(
                          "relative z-[1] mt-4 grid gap-2",
                          eventRewardItems.length === 1
                            ? "w-[164px] grid-cols-1"
                            : "w-full grid-cols-2"
                        )}
                      >
                        {eventRewardItems.map((item, index) => (
                          <motion.div
                            key={`${item.kind}-${item.level || "coins"}`}
                            initial={{ opacity: 0, y: 12, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ delay: 0.38 + index * 0.06, duration: 0.28 }}
                            className={cn(
                              "flex h-[72px] min-w-0 w-full items-center justify-center gap-1.5 rounded-[18px] border px-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.09),0_12px_28px_rgba(0,0,0,0.14)]",
                              eventRewardItems.length > 1 &&
                                eventRewardItems.length % 2 === 1 &&
                                index === eventRewardItems.length - 1 &&
                                "col-span-2 w-[calc(50%-4px)] justify-self-center",
                              item.kind === "coins"
                                ? "border-[#F8D56A]/65 bg-[#F8D56A]/12"
                                : "border-[#A993FF]/65 bg-[#A993FF]/14"
                            )}
                          >
                            <img
                              src={
                                item.kind === "coins"
                                  ? "/assets/vault/coin_icon.svg"
                                  : `/assets/search/key_level${item.level}.svg`
                              }
                              alt=""
                              className={cn(
                                item.kind === "coins"
                                  ? "h-7 w-7 object-contain"
                                  : "h-8 w-8 object-contain scale-[1.12]"
                              )}
                              draggable={false}
                            />
                            <strong
                              className={cn(
                                "text-[18px] font-semibold leading-none",
                                item.kind === "coins"
                                  ? "text-[#FFE99A]"
                                  : "text-white"
                              )}
                            >
                              {item.kind === "coins"
                                ? `+${formatCompactNumber(item.amount)}`
                                : `×${item.amount}`}
                            </strong>
                          </motion.div>
                        ))}
                      </motion.div>

                      <div className="relative z-[1] mt-auto w-[220px] max-w-full">
                        <AppModalButton
                          onClick={finishEventReward}
                          disabled={eventRewardClosing}
                          loading={eventRewardClosing}
                          className="whitespace-nowrap"
                        >
                          {t("reward.collect_button")}
                        </AppModalButton>
                      </div>
                    </div>
                  ) : eventStep === EVENT_STEP_RATING ? (
                    <div className="mx-auto flex w-full max-w-[330px] flex-col items-center">
                      <motion.button
                        type="button"
                        onClick={() => {
                          setReportMode("event");
                          setIsReportOpen(true);
                        }}
                        className="mb-3 h-7 px-3 rounded-2xl border border-white/70 bg-black/30 backdrop-blur-md flex items-center gap-2"
                        {...TAP_FEEDBACK}
                      >
                        <span className="text-white text-[14px] font-normal leading-none">
                          {t("people.report_event")}
                        </span>
                        <img
                          src="/assets/rating/report_icon.svg"
                          alt=""
                          className="w-4 h-4"
                        />
                      </motion.button>

                      <div className="w-full">
                        {renderRatingEventCard()}
                      </div>

                      <p className="mt-6 text-center text-[17px] font-semibold text-white">
                        {t("people.rate_event_title")}
                      </p>

                      <div className="mt-4 flex items-center justify-center gap-3">
                        {[1, 2, 3, 4, 5].map((value) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setEventRating(value)}
                            className="w-8 h-8 flex items-center justify-center"
                          >
                            <span
                              className={`text-3xl leading-none ${
                                value <= eventRating
                                  ? "text-white"
                                  : "text-white/35"
                              }`}
                            >
                              ★
                            </span>
                          </button>
                        ))}
                      </div>

                      {!hostFeedbackEnabled ? (
                        <div className="mt-5 w-full">
                          <AppModalButton
                            variant="secondary"
                            onClick={() => setHostFeedbackEnabled(true)}
                          >
                            {t("people.host_feedback_toggle")}
                          </AppModalButton>
                        </div>
                      ) : (
                        <textarea
                          value={hostFeedbackText}
                          onChange={(e) => setHostFeedbackText(e.target.value)}
                          placeholder={t("people.host_feedback_placeholder")}
                          className="mt-5 min-h-[116px] w-full resize-none rounded-[22px] border border-white/38 bg-[linear-gradient(145deg,rgba(18,27,42,0.88)_0%,rgba(10,22,35,0.78)_52%,rgba(8,32,49,0.82)_100%)] px-4 py-3 text-[14px] font-medium leading-6 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] outline-none transition placeholder:text-white/45 focus:border-white/55"
                          maxLength={1000}
                        />
                      )}

                      <div className="mt-5 w-[142px]">
                        <AppModalButton
                          onClick={handleEventPrimaryAction}
                          disabled={eventRatingSubmitting}
                          loading={eventRatingSubmitting}
                        >
                          {eventPrimaryButtonLabel}
                        </AppModalButton>
                      </div>
                    </div>
                  ) : eventStep === EVENT_STEP_ATTENDANCE ? (
                    <div className="mx-auto flex h-full w-full max-w-[330px] flex-col pb-1 pt-[34px]">
                      <div className="flex flex-col items-center text-center">
                        <div className="flex h-[52px] w-[52px] items-center justify-center rounded-full border border-white/55 bg-white/8 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                          <SafetyIntroIcon />
                        </div>
                        <h3 className="mt-5 text-[25px] font-semibold leading-tight text-white">
                          {t("people.unregistered_attendees_title")}
                        </h3>
                        <p className="mt-3 text-[14px] font-semibold leading-[1.45] text-white/76">
                          {t("people.unregistered_attendees_body")}
                        </p>
                      </div>

                      <div className="mt-auto w-full pb-1">
                        <p className="mx-auto mb-8 text-center text-[17px] font-semibold leading-snug text-white">
                          {t("people.unregistered_attendees_question")}
                        </p>

                        <div className="grid w-full grid-cols-2 gap-3">
                          <AppModalButton
                            variant="secondary"
                            onClick={() => handleUnregisteredAttendeesAnswer(true)}
                            disabled={eventRatingSubmitting}
                            loading={eventRatingSubmitting && sawUnregisteredAttendees === true}
                          >
                            {t("people.answer_yes")}
                          </AppModalButton>
                          <AppModalButton
                            onClick={() => handleUnregisteredAttendeesAnswer(false)}
                            disabled={eventRatingSubmitting}
                            loading={eventRatingSubmitting && sawUnregisteredAttendees === false}
                          >
                            {t("people.answer_no")}
                          </AppModalButton>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mx-auto flex h-full w-full max-w-[330px] flex-col pb-1 pt-[34px]">
                      <div className="flex flex-col items-center text-center">
                        <div className="flex h-[52px] w-[52px] items-center justify-center rounded-full border border-[#F8D56A]/55 bg-[#F8D56A]/12 text-[#FFE99A] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                          <SafetyIntroIcon />
                        </div>
                        <h3 className="mt-5 text-[25px] font-semibold leading-tight text-white">
                          {t("people.unregistered_attendees_confirm_title")}
                        </h3>
                        <p className="mt-4 text-[15px] font-semibold leading-[1.5] text-white/78">
                          {t("people.unregistered_attendees_confirm_body")}
                        </p>
                      </div>

                      <div className="mt-auto w-full pb-1">
                        <p className="mx-auto mb-5 max-w-[290px] text-center text-[13px] font-medium leading-[1.45] text-white/60">
                          {t("people.unregistered_attendees_confirm_hint")}
                        </p>
                        <SlideToConfirm
                          label={t("people.slide_to_confirm")}
                          confirmedLabel={t("people.confirmed")}
                          ariaLabel={t("people.unregistered_attendees_confirm_action")}
                          disabled={eventRatingSubmitting}
                          onConfirm={handleUnregisteredAttendeesConfirm}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
              // ---------- PEOPLE RATING PHASE ----------
                <>
                  {/* Avatar with thin bordered frame */}
                  <div
                    className={avatarFrameClassName}
                  >
                    <img
                      src={current?.avatarUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                  </div>

                  {current?.hasPremiumSubscription && !isPeopleReportMode ? (
                    <motion.button
                      type="button"
                      onClick={openPremiumPage}
                      className={premiumUserPillClassName}
                      {...TAP_FEEDBACK}
                    >
                      <RatingPremiumCrown />
                      <span className="text-white text-[13px] font-semibold leading-none">Premium User</span>
                    </motion.button>
                  ) : null}

                  <div className={personCodeClassName}>{current?.code}</div>
                  {!isPeopleReportMode && current?.username && (
                    <div className="vg-rating-person-name">@{String(current.username).replace(/^@/, "")}</div>
                  )}

                  {isPeopleReportMode ? (
                    <div className="absolute left-0 bottom-[16px] top-[408px] z-[5] flex w-full flex-col px-[18px]">
                      <div className="relative h-[42px]">
                        <motion.button
                          type="button"
                          onClick={handleBack}
                          className="absolute left-0 top-[2px] flex h-8 w-8 items-center justify-center rounded-full border border-white/42 bg-black/28 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-md"
                          aria-label={t("people.back_button")}
                          disabled={peopleReportSubmitting}
                          {...TAP_FEEDBACK}
                        >
                          <ModalBackIcon />
                        </motion.button>
                        <p className="absolute inset-x-[38px] top-0 flex h-[42px] items-center justify-center text-center text-[17px] font-semibold leading-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]">
                          {isPeopleReportDetailsStep
                            ? t("people.report.details_label")
                            : t("people.report.reason_label")}
                        </p>
                      </div>

                      <AnimatePresence mode="wait">
                        {!isPeopleReportDetailsStep ? (
                          <motion.div
                            key="people-report-reasons"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.16, ease: "easeOut" }}
                            className="mt-3 grid grid-cols-2 gap-2"
                          >
                            {peopleReportReasonOptions.map((reason) => (
                              <motion.button
                                key={reason.value}
                                type="button"
                                onClick={() => {
                                  setPeopleReportReason(reason.value);
                                  setPeopleReportStep("details");
                                  setPeopleReportComment("");
                                  setPeopleReportError("");
                                }}
                                className={cn(
                                  "flex h-10 min-w-0 items-center justify-center rounded-full border px-3 text-center text-[12px] font-semibold leading-tight text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.10)] backdrop-blur-md transition",
                                  isPremiumTheme
                                    ? "border-white/45 bg-white/13"
                                    : "border-white/70 bg-black/30"
                                )}
                                {...TAP_FEEDBACK}
                              >
                                <span className="block whitespace-normal">
                                  {reason.label}
                                </span>
                              </motion.button>
                            ))}
                          </motion.div>
                        ) : (
                          <motion.div
                            key="people-report-details"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.16, ease: "easeOut" }}
                            className="mt-1 flex min-h-0 flex-1 flex-col items-center"
                          >
                            <textarea
                              value={peopleReportComment}
                              onChange={(event) => setPeopleReportComment(event.target.value)}
                              placeholder={t("people.report.details_placeholder")}
                              maxLength={800}
                              className="h-[84px] w-full resize-none rounded-[20px] border border-white/34 bg-[rgba(7,16,28,0.58)] px-4 py-3 text-[13px] font-medium leading-[18px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] outline-none backdrop-blur-md placeholder:text-white/56 focus:border-white/62"
                            />

                            {peopleReportError ? (
                              <p className="mt-1 text-center text-[11px] font-semibold leading-tight text-red-200">
                                {peopleReportError}
                              </p>
                            ) : null}

                            <motion.button
                              type="button"
                              onClick={handlePeopleReportSubmit}
                              disabled={!peopleReportReason || peopleReportSubmitting}
                              className="mt-3 flex h-10 w-[172px] shrink-0 items-center justify-center rounded-full border border-white/70 bg-white px-4 text-[13px] font-semibold text-[#142131] shadow-[0_10px_24px_rgba(0,0,0,0.20)] transition disabled:bg-white/35 disabled:text-white/55"
                              {...TAP_FEEDBACK}
                            >
                              {peopleReportSubmitting
                                ? t("people.report.sending")
                                : t("people.report.submit")}
                            </motion.button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <>
                      {/* Safety actions sit under the public username. */}
                      <div className="vg-rating-person-safety">
                        <motion.button
                          type="button"
                          onClick={() => onAction("report", 0)}
                          disabled={peopleActionSubmitting}
                          className={personSafetyPillClassName}
                          {...TAP_FEEDBACK}
                        >
                          <img
                            src="/assets/rating/report_icon.svg"
                            alt=""
                          />
                          <span>{t("people.report_user")}</span>
                        </motion.button>

                        <motion.button
                          type="button"
                          onClick={() => onAction("photo_bad", 0)}
                          disabled={peopleActionSubmitting}
                          className={personSafetyPillClassName}
                          {...TAP_FEEDBACK}
                        >
                          <img
                            src="/assets/rating/bad_photo_icon.svg"
                            alt=""
                          />
                          <span>{t("people.bad_photo")}</span>
                        </motion.button>
                      </div>

                      {/* Action buttons row */}
                      <div className="absolute left-0 bottom-[18px] w-full px-[18px]">
                        {currentEvaluationMode === "skill" ? (
                      // Skill mode
                      <div className="flex items-end justify-between">
                        {/* Skip */}
                        <motion.button
                          type="button"
                          onClick={() => onAction("skip", 0)}
                          disabled={peopleActionSubmitting}
                          className="flex flex-col items-center gap-2"
                          {...TAP_FEEDBACK}
                        >
                          <div className={getActionSurfaceClassName("muted")}>
                            <img
                              src="/assets/rating/skip_icon.svg"
                              alt=""
                              className="w-6 h-6"
                            />
                          </div>
                          <div className={actionLabelClassName}>
                            {t("modal.actions.skip.label")}
                          </div>
                        </motion.button>

                        {/* Skill levels */}
                        <div className="flex flex-col items-center gap-2 mb-[2px]">
                          <motion.button
                            type="button"
                            onClick={() => onAction("skill_level_1", 0)}
                            disabled={peopleActionSubmitting}
                            className={skillButtonClassName}
                            {...TAP_FEEDBACK}
                          >
                            <span className="text-white text-[13px] font-semibold tracking-wide">
                              {t("modal.sport_levels.beginner.label")}
                            </span>
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={() => onAction("skill_level_2", 0)}
                            disabled={peopleActionSubmitting}
                            className={skillButtonClassName}
                            {...TAP_FEEDBACK}
                          >
                            <span className="text-white text-[13px] font-semibold tracking-wide">
                              {t("modal.sport_levels.intermediate.label")}
                            </span>
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={() => onAction("skill_level_3", 0)}
                            disabled={peopleActionSubmitting}
                            className={skillButtonClassName}
                            {...TAP_FEEDBACK}
                          >
                            <span className="text-white text-[13px] font-semibold tracking-wide">
                              {t("modal.sport_levels.advanced.label")}
                            </span>
                          </motion.button>
                        </div>

                        {/* Mark */}
                        <motion.button
                          type="button"
                          onClick={() => onAction("mark", 0)}
                          disabled={peopleActionSubmitting}
                          className="flex flex-col items-center gap-2"
                          {...TAP_FEEDBACK}
                        >
                          <div className={getActionSurfaceClassName(isMarked ? "active" : "muted")}>
                            <img
                              src={
                                isMarked
                                  ? "/assets/rating/mark_icon_on.svg"
                                  : "/assets/rating/mark_icon_off.svg"
                              }
                              alt=""
                              className="w-6 h-6"
                            />
                          </div>
                          <div className={actionLabelClassName}>
                            {t("modal.actions.mark.label")}
                          </div>
                        </motion.button>
                      </div>
                        ) : (
                      // Networking mode
                      <div className="flex items-end justify-between">
                        {/* Skip */}
                        <motion.button
                          type="button"
                          onClick={() =>
                            onAction(
                              isOptionalPeopleStage ? "not_interacted" : "skip",
                              0
                            )
                          }
                          disabled={peopleActionSubmitting}
                          className="flex flex-col items-center gap-2"
                          {...TAP_FEEDBACK}
                        >
                          <div className={getActionSurfaceClassName("muted")}>
                            <img
                              src="/assets/rating/skip_icon.svg"
                              alt=""
                              className="w-6 h-6"
                            />
                          </div>
                          <div className={actionLabelClassName}>
                            {isOptionalPeopleStage
                              ? t("people_flow.not_interacted")
                              : t("modal.actions.skip.label")}
                          </div>
                        </motion.button>

                        {/* Not my vibe */}
                        <motion.button
                          type="button"
                          onClick={() => onAction("dislike", 1)}
                          disabled={peopleActionSubmitting}
                          className="flex flex-col items-center gap-2"
                          {...TAP_FEEDBACK}
                        >
                          <div className={getActionSurfaceClassName("secondary")}>
                            <img
                              src="/assets/rating/social_not_my_vibe_v3.svg"
                              alt=""
                              className="w-6 h-6"
                            />
                          </div>
                          <div className={actionLabelClassName}>
                            {t("modal.actions.next.label")}
                          </div>
                        </motion.button>

                        {/* My vibe */}
                        <motion.button
                          type="button"
                          onClick={() => onAction("like", 1)}
                          disabled={peopleActionSubmitting}
                          className="flex flex-col items-center gap-2"
                          {...TAP_FEEDBACK}
                        >
                          <div className={getActionSurfaceClassName("primary")}>
                            <img
                              src="/assets/rating/social_my_vibe_v3.svg"
                              alt=""
                              className="w-6 h-6"
                            />
                          </div>
                          <div className={actionLabelClassName}>
                            {t("modal.actions.like.label")}
                          </div>
                        </motion.button>

                        {/* Anonymous discomfort signal */}
                        <motion.button
                          type="button"
                          onClick={() => onAction("discomfort", 1)}
                          disabled={peopleActionSubmitting}
                          className="flex flex-col items-center gap-2"
                          {...TAP_FEEDBACK}
                        >
                          <div className={getActionSurfaceClassName("muted")}>
                            <img
                              src="/assets/rating/social_discomfort_v3.svg"
                              alt=""
                              className="w-6 h-6"
                            />
                          </div>
                          <div className={actionLabelClassName}>
                            {t("modal.actions.discomfort.label")}
                          </div>
                        </motion.button>
                      </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </RatingCardSurface>

      <ReportForm
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        onSubmit={handleReportSubmit}
        mode={reportMode}
      />
    </RatingCardMotion>
  );

  const desktopModal = (
    <AnimatePresence>
      {isVisible ? (
        <motion.main
          className={`fixed inset-0 z-[13020] flex justify-center overflow-hidden ${
            modalCardLayout.preferTopAlignedLayout
              ? "items-start overflow-y-auto overscroll-contain"
              : "items-center"
          }`}
          initial="hidden"
          animate="visible"
          exit="exit"
          role="presentation"
        >
          <motion.button
            type="button"
            onClick={handleCloseClick}
            className={`absolute inset-0 cursor-default ${MODAL_BACKDROP_CLASS}`}
            variants={modalBackdropVariants}
            aria-label={t("modal.close_button")}
          />

          <motion.div
            className={`relative z-[13021] pointer-events-auto px-3 ${
              modalCardLayout.preferTopAlignedLayout ? "py-2" : "py-6"
            }`}
            style={
              modalCardLayout.preferTopAlignedLayout
                ? {
                    paddingTop: "max(env(safe-area-inset-top,0px), 8px)",
                    paddingBottom: modalCardLayout.bottomNavReservePx > 0
                      ? "calc(var(--bottom-nav-clearance, 112px) + 8px)"
                      : "max(env(safe-area-inset-bottom,0px), 8px)",
                  }
                : modalCardLayout.bottomNavReservePx > 0
                ? {
                    paddingBottom: "var(--bottom-nav-clearance, 112px)",
                  }
                : undefined
            }
            variants={modalPanelVariants}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={ratingModalTitle}
          >
            <motion.div variants={modalContentRootVariants}>
              <div
                style={{
                  transform:
                    modalCardLayout.scale < 0.999
                      ? `scale(${modalCardLayout.scale})`
                      : undefined,
                  transformOrigin: "top center",
                }}
              >
                {modalBody}
              </div>
            </motion.div>
          </motion.div>
        </motion.main>
      ) : null}
    </AnimatePresence>
  );

  return createPortal(desktopModal, root);
};

export default RatingModal;
